import { REMEMBER_ME_STORAGE_KEY } from "../../config/constants";

export function decodeJwtPayload(token) {
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replaceAll("-", "+").replaceAll("_", "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const payload = atob(padded);
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

export function getRoleFromToken(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return "User";

  const roleClaim = payload.role;
  if (typeof roleClaim === "string" && roleClaim.trim()) {
    return roleClaim;
  }

  const rolesClaim = payload.roles;
  if (Array.isArray(rolesClaim) && rolesClaim.length > 0) {
    const firstRole = rolesClaim[0];
    if (typeof firstRole === "string" && firstRole.trim()) {
      return firstRole;
    }
  }

  const authoritiesClaim = payload.authorities;
  if (Array.isArray(authoritiesClaim) && authoritiesClaim.length > 0) {
    const firstAuthority = authoritiesClaim[0];
    if (typeof firstAuthority === "string" && firstAuthority.trim()) {
      return firstAuthority;
    }
  }

  return "User";
}

/** Derives the normalized session/user info from a login API response. */
export function buildLoginSession(
  data,
  fallbackUserId,
) {
  const role = data.user?.roleName || getRoleFromToken(data.token) || "User";
  const firstName = data.user?.firstName || fallbackUserId;
  const lastName = data.user?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const userId = data.user?.userId || fallbackUserId;
  const requiresPasswordReset = Boolean(
    data.adminResetPassword || data.user?.adminResetPassword,
  );

  return {
    loginUser: {
      name: fullName || firstName,
      firstName,
      lastName,
      email: userId,
      role,
    },
    userId,
    role,
    requiresPasswordReset,
  };
}

/** Persists the authenticated session (token + user info) to sessionStorage. */
export function persistLoginSession(
  data,
  session,
) {
  sessionStorage.setItem("token", data.token);
  sessionStorage.setItem("tokenType", data.tokenType || "Bearer");
  const expiresIn = Number(data.expiresIn);
  if (expiresIn > 0) {
    sessionStorage.setItem(
      "tokenExpiryInSeconds",
      String(Math.floor(Date.now() / 1000) + expiresIn),
    );
  } else {
    sessionStorage.removeItem("tokenExpiryInSeconds");
  }
  sessionStorage.setItem("role", session.role);
  sessionStorage.setItem("firstName", session.loginUser.firstName || "");
  sessionStorage.setItem("lastName", session.loginUser.lastName || "");
  sessionStorage.setItem("userName", session.loginUser.name);
  sessionStorage.setItem("userId", session.userId);
}

export function applyRememberMe(remember, userId) {
  if (remember) {
    localStorage.setItem(REMEMBER_ME_STORAGE_KEY, userId);
  } else {
    localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
  }
}

export function getRememberedUserId() {
  return localStorage.getItem(REMEMBER_ME_STORAGE_KEY);
}
